import { type FC } from 'react';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

const RiskDisclosurePage: FC = () => {
  usePageMeta({ title: 'Risk Disclosure', description: 'Important information about the risks associated with trading financial markets using Quanthop tools.' });

  return (
    <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
      <PublicNavBar />

      {/* ════════════════ HERO ════════════════ */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-[680px] mx-auto relative z-10 text-center">
          <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
            Risk Disclosure
          </h1>

          <p className="text-[15px] text-gray-300/70 leading-[1.7]">
            Important information about the risks associated with trading
            financial markets.
          </p>

          <p className="text-[13px] text-gray-400/60 mt-6">
            Last updated: 8 March 2026
          </p>
        </div>
      </section>

      {/* ════════════════ CONTENT ════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
        <div className="max-w-[720px] mx-auto text-gray-700 text-[15px] leading-[1.8]">
          <p className="mb-10 text-gray-500">
            The Quanthop platform provides research tools for developing and
            analysing systematic trading strategies. Trading financial markets
            involves significant risk.
          </p>

          {/* ── 1 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            1. No Investment Advice
          </h2>
          <p className="mb-3">Quanthop does not provide:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>investment advice</li>
            <li>financial advice</li>
            <li>trading recommendations</li>
          </ul>
          <p className="mb-4">
            The platform is intended solely for research and analysis.
          </p>
          <p className="font-medium text-gray-900">
            Users are responsible for their own trading decisions.
          </p>

          {/* ── 2 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            2. No Guarantee of Results
          </h2>
          <p className="mb-4">
            Backtests, simulations, and performance metrics generated by the
            platform are based on historical data and model assumptions.
          </p>
          <p className="mb-4 font-medium text-gray-900">
            Historical results do not guarantee future performance.
          </p>
          <p>
            Trading strategies that perform well in simulations may perform
            differently in live markets.
          </p>

          {/* ── 3 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            3. Risk of Loss
          </h2>
          <p className="mb-4">
            Trading financial instruments such as stocks, cryptocurrencies,
            derivatives, and foreign exchange involves risk of substantial
            financial loss.
          </p>
          <p>
            Users should carefully consider whether trading is suitable for
            their financial situation.
          </p>

          {/* ── 4 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            4. Model and Data Limitations
          </h2>
          <p className="mb-3">Research tools may rely on:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>historical datasets</li>
            <li>simulated execution</li>
            <li>assumptions about market conditions</li>
          </ul>
          <p>
            These models may not fully reflect real market behaviour.
          </p>

          {/* ── 5 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            5. User Responsibility
          </h2>
          <p className="mb-3">Users are solely responsible for:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>evaluating trading strategies</li>
            <li>managing financial risk</li>
            <li>complying with applicable regulations</li>
            <li>executing trades with brokers or exchanges</li>
          </ul>
          <p className="font-medium text-gray-900">
            Quanthop does not execute trades on behalf of users.
          </p>

          {/* ── 6 ── */}
          <h2 className="text-[18px] font-bold text-gray-950 tracking-[-0.01em] mb-4 mt-14">
            6. Educational and Research Use
          </h2>
          <p className="mb-3">The platform is designed for:</p>
          <ul className="list-disc pl-6 space-y-1.5 mb-4">
            <li>strategy research</li>
            <li>algorithm development</li>
            <li>statistical analysis</li>
          </ul>
          <p>It should not be interpreted as financial advice.</p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default RiskDisclosurePage;
